import React from "react";
import { Archive, Copy, Eye, Pin, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  type AdminAnnouncement,
  type AccountTier,
  type AnnouncementSeverity,
  getAnnouncementStatus,
} from "@/lib/announcements";
import { tierDisplayName } from "@/lib/tier";
import { TIERS, statusBadgeClass, severityBadgeClass } from "./announcementHelpers";

type StatusFilter = "all" | "draft" | "scheduled" | "live" | "expired";
type SortBy = "newest" | "oldest" | "most_read";
type AudienceFilter = AccountTier | "all" | "all_users";

interface Props {
  loading: boolean;
  items: AdminAnnouncement[];
  filteredItems: AdminAnnouncement[];
  readsCounts: Record<string, number>;
  editingId: string | null;
  searchQuery: string;
  setSearchQuery: (v: string) => void;
  statusFilter: StatusFilter;
  setStatusFilter: (v: StatusFilter) => void;
  severityFilter: AnnouncementSeverity | "all";
  setSeverityFilter: (v: AnnouncementSeverity | "all") => void;
  audienceFilter: AudienceFilter;
  setAudienceFilter: (v: AudienceFilter) => void;
  sortBy: SortBy;
  setSortBy: (v: SortBy) => void;
  hideExpired: boolean;
  setHideExpired: React.Dispatch<React.SetStateAction<boolean>>;
  expiredCount: number;
  bulkMarking: boolean;
  onBulkMarkRead: () => void;
  onStartEdit: (a: AdminAnnouncement) => void;
  onPreview: (a: AdminAnnouncement) => void;
  onDuplicate: (a: AdminAnnouncement, e: React.MouseEvent) => void;
}

export const AnnouncementsListCard: React.FC<Props> = ({
  loading,
  items,
  filteredItems,
  readsCounts,
  editingId,
  searchQuery,
  setSearchQuery,
  statusFilter,
  setStatusFilter,
  severityFilter,
  setSeverityFilter,
  audienceFilter,
  setAudienceFilter,
  sortBy,
  setSortBy,
  hideExpired,
  setHideExpired,
  expiredCount,
  bulkMarking,
  onBulkMarkRead,
  onStartEdit,
  onPreview,
  onDuplicate,
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">All announcements</CardTitle>
        <CardDescription>
          {loading
            ? "Loading…"
            : filteredItems.length === items.length
              ? `${items.length} total`
              : `${filteredItems.length} of ${items.length}`}
        </CardDescription>
        <div className="relative pt-2">
          <Search className="absolute left-2.5 top-1/2 mt-1 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search title or body…"
            className="h-9 pl-8"
          />
        </div>
        <div className="grid gap-2 pt-2 sm:grid-cols-2">
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
            <SelectTrigger className="h-9"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="live">Live</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectContent>
          </Select>
          <Select value={severityFilter} onValueChange={(v) => setSeverityFilter(v as AnnouncementSeverity | "all")}>
            <SelectTrigger className="h-9"><SelectValue placeholder="Severity" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All severities</SelectItem>
              <SelectItem value="info">Info</SelectItem>
              <SelectItem value="success">Success</SelectItem>
              <SelectItem value="warning">Warning</SelectItem>
            </SelectContent>
          </Select>
          <Select value={audienceFilter} onValueChange={(v) => setAudienceFilter(v as AudienceFilter)}>
            <SelectTrigger className="h-9"><SelectValue placeholder="Audience" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All audiences</SelectItem>
              <SelectItem value="all_users">All users</SelectItem>
              {TIERS.map((t) => (
                <SelectItem key={t} value={t}>Tier: {tierDisplayName(t)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortBy)}>
            <SelectTrigger className="h-9"><SelectValue placeholder="Sort" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest first</SelectItem>
              <SelectItem value="oldest">Oldest first</SelectItem>
              <SelectItem value="most_read">Most read</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button
            variant={hideExpired ? "outline" : "secondary"}
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => setHideExpired((v) => !v)}
            title={hideExpired ? "Show expired announcements" : "Hide expired announcements"}
          >
            <Archive className="h-3 w-3" />
            {hideExpired ? `Show expired${expiredCount > 0 ? ` (${expiredCount})` : ""}` : "Hide expired"}
          </Button>
          {(severityFilter !== "all" || audienceFilter !== "all" || statusFilter !== "all" || searchQuery !== "") && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => {
                setSeverityFilter("all");
                setAudienceFilter("all");
                setStatusFilter("all");
                setSearchQuery("");
              }}
            >
              <X className="h-3 w-3" /> Clear filters
            </Button>
          )}
        </div>
        <div className="pt-2">
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-full"
            disabled={bulkMarking || loading || filteredItems.length === 0}
            onClick={onBulkMarkRead}
          >
            <Eye className="h-3.5 w-3.5" />
            {bulkMarking ? "Marking…" : `Mark ${filteredItems.length} filtered as read`}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2 p-2">
        {loading ? (
          <Skeleton className="h-24 w-full" />
        ) : filteredItems.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">
            {items.length === 0 ? "No announcements yet." : "No announcements match these filters."}
          </p>
        ) : (
          filteredItems.map((a) => {
            const status = getAnnouncementStatus(a);
            const isEditing = editingId === a.id;
            return (
              <div
                key={a.id}
                role="button"
                tabIndex={0}
                onClick={() => onStartEdit(a)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onStartEdit(a);
                  }
                }}
                className={`w-full text-left rounded-md border px-3 py-2 transition-colors cursor-pointer ${
                  isEditing ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {a.is_pinned && <Pin className="h-3 w-3 text-amber-500 shrink-0" />}
                      <span className="font-medium truncate">{a.title || "(untitled)"}</span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <Badge variant="secondary" className={statusBadgeClass[status]}>
                        {status}
                      </Badge>
                      <Badge variant="secondary" className={severityBadgeClass[a.severity]}>
                        {a.severity}
                      </Badge>
                      <Badge variant="outline" className="text-xs">
                        {a.audience === "all" ? "All users" : `Tier: ${tierDisplayName(a.audience_tier)}`}
                      </Badge>
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                        <Eye className="h-3 w-3" /> {readsCounts[a.id] ?? 0}
                      </span>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      title="Preview as user"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPreview(a);
                      }}
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      title="Duplicate as draft"
                      onClick={(e) => onDuplicate(a, e)}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
};
